import {
  applyEntityDelta,
  type ChunkData,
  type ClientCameraUpdateMessage,
  type ClientCancelConstructionMessage,
  type ClientCancelCraftMessage,
  type ClientChunkRequestMessage,
  type ClientConnectMessage,
  type ClientConstructionOrderMessage,
  type ClientCraftOrderMessage,
  type ClientInputMessage,
  type ClientOpenWorkstationMessage,
  type ClientPawnCommandMessage,
  type ClientPawnSelectionMessage,
  type ClientPingMessage,
  type ClientRespawnMessage,
  deserializeMessage,
  type EntitySnapshot,
  EntityType,
  getItemIcon,
  getItemName,
  type ItemTypeValue,
  MessageType,
  type ObjectTypeValue,
  PawnCommandType,
  PROTOCOL_VERSION,
  type RotStageValue,
  type ServerChunkDataMessage,
  type ServerConstructionSiteCancelMessage,
  type ServerConstructionSiteCompleteMessage,
  type ServerConstructionSiteSpawnMessage,
  type ServerConstructionSiteUpdateMessage,
  type ServerCorpseDespawnMessage,
  type ServerCorpseSpawnMessage,
  type ServerCorpseUpdateMessage,
  type ServerCraftOrderCancelledMessage,
  type ServerCraftOrderCompleteMessage,
  type ServerCraftOrderQueuedMessage,
  type ServerCraftOrderUpdateMessage,
  type ServerDeltaSnapshotMessage,
  type ServerEntityDiedMessage,
  type ServerEntitySpawnMessage,
  type ServerGroundItemDespawnMessage,
  type ServerGroundItemSpawnMessage,
  type ServerGroundItemUpdateMessage,
  type ServerInventoryUpdateMessage,
  type ServerMessage,
  type ServerPongMessage,
  type ServerProjectileDespawnMessage,
  type ServerProjectileHitMessage,
  type ServerProjectileSpawnMessage,
  type ServerQuestsFullMessage,
  type ServerQuestUpdateMessage,
  type ServerRespawnMessage,
  type ServerSkillXpGainMessage,
  type ServerSnapshotMessage,
  type ServerStockpileFullMessage,
  type ServerStockpileUpdateMessage,
  type ServerTerritoryClaimedMessage,
  type ServerTerritoryLostMessage,
  type ServerTerritoryUpdateMessage,
  type ServerTimeUpdateMessage,
  type ServerWelcomeMessage,
  type ServerWorkstationDataMessage,
  type ServerWorldObjectRemoveMessage,
  type ServerWorldObjectUpdateMessage,
  type SkillSnapshot,
  serializeMessage,
  type TerritoryTypeValue,
  type WorkstationTypeValue,
} from '@dreamgame/shared';
import type { InputState } from '../input/InputManager';
import {
  addObjectToChunk,
  markChunkPending,
  removeObjectFromChunk,
  setChunk,
  updateObjectInChunk,
} from '../state/chunks';
import { clearColonyLost, setColonyLost } from '../state/colonyLost';
import { clearAllQueues, clearQueue, enqueueCommands } from '../state/commandQueue';
import {
  incrementReconnectAttempts,
  reconnectAttempts,
  resetConnection as resetConnectionState,
  resetReconnectAttempts,
  setConnected,
  setConnecting,
  setDisconnected,
  setFailed,
  setServerUrl,
  updateLatency,
} from '../state/connection';
import {
  removeConstructionSite,
  setConstructionSite,
  setConstructionSites,
  updateConstructionSite,
} from '../state/construction';
import { addCorpse, removeCorpse, updateCorpse } from '../state/corpses';
import {
  handleCraftOrderCancelled,
  handleCraftOrderComplete,
  handleCraftOrderQueued,
  handleCraftOrderUpdate,
  handleWorkstationData,
  handleWorkstationWorldObjectUpdate,
} from '../state/crafting';
import {
  addPawn,
  getPawn,
  type PawnData,
  removePawn,
  setPlayerId,
  updatePawn,
} from '../state/entities';
import { isProfilerEnabled, recordNetworkMessage } from '../state/frameProfiler';
import {
  removeGroundItem,
  setGroundItem,
  setGroundItems,
  updateGroundItemQuantity,
} from '../state/groundItems';
import {
  addRttSample,
  clearEntityStates,
  DEFAULT_RECONNECT_CONFIG,
  deleteEntityState,
  getEntityState,
  incrementInputSequence,
  jitterMs,
  lastPingTime,
  PING_INTERVAL_MS,
  reconnectEnabled,
  resetLatencyStats,
  rttMs,
  serverTick,
  setEntityState,
  setReconnectEnabled,
  updateLastAcknowledgedInput,
  updatePingTime,
  updateServerTick,
  updateServerTickRate,
} from '../state/network';
import { addNpc, getNpc, removeNpc, updateNpc } from '../state/npcs';
import {
  EquipmentSlot,
  equipItem,
  INVENTORY_SIZE,
  type ItemData,
  ItemRarity,
  setInventory,
} from '../state/pawnGear';
import { addProjectile, removeProjectile } from '../state/projectiles';
import { setQuests, updateQuest } from '../state/quests';
import { onNpcRemoved, onPawnRemoved } from '../state/selection';
import { getSessionId } from '../state/session';
import {
  clearSkillXpState,
  recordSkillXpGain,
  seedSkillXpFromLevel,
  upsertSkillTotalXp,
} from '../state/skillXp';
import { setStockpile, updateStockpileItem } from '../state/stockpile';
import { removeTerritory, setTerritories, setTerritory } from '../state/territories';
import { setGameTime } from '../state/time';
import { addXpDrop, clearXpDrops } from '../state/xpDrops';

// ============================================================================
// Types
// ============================================================================

interface ConnectOptions {
  url: string;
  playerName?: string;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onWelcome?: ({ message }: { message: ServerWelcomeMessage }) => void;
}

// Non-reactive resources (WebSocket, timers) - kept as module-level references
interface NetworkResources {
  socket: WebSocket | null;
  pingIntervalId: ReturnType<typeof setInterval> | null;
  reconnectTimeoutId: ReturnType<typeof setTimeout> | null;
  url: string;
  playerName: string;
  callbacks: {
    onConnect: (() => void) | null;
    onDisconnect: (() => void) | null;
    onWelcome: (({ message }: { message: ServerWelcomeMessage }) => void) | null;
  };
}

// Non-reactive resources (not signals - these are transient)
const resources: NetworkResources = {
  socket: null,
  pingIntervalId: null,
  reconnectTimeoutId: null,
  url: '',
  playerName: 'Player',
  callbacks: {
    onConnect: null,
    onDisconnect: null,
    onWelcome: null,
  },
};

// ============================================================================
// Message Queue
// ============================================================================
// Network messages are queued as raw ArrayBuffers and drained once per frame
// inside the game loop. This prevents async WebSocket processing from running
// between rAF callbacks and delaying the next frame (which causes periodic
// jitter aligned with the server's 10Hz snapshot rate).
// ============================================================================

const _messageQueue: ArrayBuffer[] = [];

/**
 * Drain all queued network messages. Call once per frame at the start of the
 * game loop so all deserialization + state mutation is budgeted within the rAF.
 */
export const drainNetworkMessages = (): void => {
  const profiling = isProfilerEnabled();
  for (let i = 0; i < _messageQueue.length; i++) {
    const buf = _messageQueue[i];
    if (buf) {
      const start = profiling ? performance.now() : 0;
      const message = deserializeMessage(new Uint8Array(buf));
      handleMessage({ message: message as ServerMessage });
      if (profiling) {
        recordNetworkMessage({ durationMs: performance.now() - start });
      }
    }
  }
  _messageQueue.length = 0;
};

// ============================================================================
// Message Handlers
// ============================================================================

const handleMessage = ({ message }: { message: ServerMessage }): void => {
  switch (message.type) {
    case MessageType.SERVER_WELCOME:
      handleWelcome({ message });
      break;
    case MessageType.SERVER_SNAPSHOT:
      handleSnapshot({ message });
      break;
    case MessageType.SERVER_DELTA_SNAPSHOT:
      handleDeltaSnapshot({ message });
      break;
    case MessageType.SERVER_ENTITY_SPAWN:
      handleEntitySpawn({ message });
      break;
    case MessageType.SERVER_ENTITY_DESPAWN:
      handleEntityDespawn({ networkId: message.networkId });
      break;
    case MessageType.SERVER_CHUNK_DATA:
      handleChunkData({ message });
      break;
    case MessageType.SERVER_PONG:
      handlePong({ message });
      break;
    case MessageType.SERVER_GROUND_ITEM_SPAWN:
      handleGroundItemSpawn({ message });
      break;
    case MessageType.SERVER_GROUND_ITEM_DESPAWN:
      handleGroundItemDespawn({ message });
      break;
    case MessageType.SERVER_GROUND_ITEM_UPDATE:
      handleGroundItemUpdate({ message });
      break;
    case MessageType.SERVER_CONSTRUCTION_SITE_SPAWN:
      handleConstructionSiteSpawn({ message });
      break;
    case MessageType.SERVER_CONSTRUCTION_SITE_UPDATE:
      handleConstructionSiteUpdate({ message });
      break;
    case MessageType.SERVER_CONSTRUCTION_SITE_COMPLETE:
      handleConstructionSiteComplete({ message });
      break;
    case MessageType.SERVER_CONSTRUCTION_SITE_CANCEL:
      handleConstructionSiteCancel({ message });
      break;
    case MessageType.SERVER_INVENTORY_UPDATE:
      handleInventoryUpdate({ message });
      break;
    case MessageType.SERVER_TERRITORY_CLAIMED:
      handleTerritoryClaimed({ message });
      break;
    case MessageType.SERVER_TERRITORY_LOST:
      handleTerritoryLost({ message });
      break;
    case MessageType.SERVER_TERRITORY_UPDATE:
      handleTerritoryUpdate({ message });
      break;
    case MessageType.SERVER_ENTITY_DIED:
      handleEntityDied({ message });
      break;
    case MessageType.SERVER_CORPSE_SPAWN:
      handleCorpseSpawn({ message });
      break;
    case MessageType.SERVER_CORPSE_UPDATE:
      handleCorpseUpdate({ message });
      break;
    case MessageType.SERVER_CORPSE_DESPAWN:
      handleCorpseDespawn({ message });
      break;
    case MessageType.SERVER_WORKSTATION_DATA:
      handleServerWorkstationData({ message });
      break;
    case MessageType.SERVER_CRAFT_ORDER_QUEUED:
      handleServerCraftOrderQueued({ message });
      break;
    case MessageType.SERVER_CRAFT_ORDER_UPDATE:
      handleServerCraftOrderUpdate({ message });
      break;
    case MessageType.SERVER_CRAFT_ORDER_COMPLETE:
      handleServerCraftOrderComplete({ message });
      break;
    case MessageType.SERVER_CRAFT_ORDER_CANCELLED:
      handleServerCraftOrderCancelled({ message });
      break;
    case MessageType.SERVER_STOCKPILE_FULL:
      handleStockpileFull({ message });
      break;
    case MessageType.SERVER_STOCKPILE_UPDATE:
      handleStockpileUpdate({ message });
      break;
    case MessageType.SERVER_PROJECTILE_SPAWN:
      handleProjectileSpawn({ message });
      break;
    case MessageType.SERVER_PROJECTILE_DESPAWN:
      handleProjectileDespawn({ message });
      break;
    case MessageType.SERVER_PROJECTILE_HIT:
      handleProjectileHit({ message });
      break;
    case MessageType.SERVER_TIME_UPDATE:
      handleTimeUpdate({ message });
      break;
    case MessageType.SERVER_QUESTS_FULL:
      handleQuestsFull({ message: message as ServerQuestsFullMessage });
      break;
    case MessageType.SERVER_QUEST_UPDATE:
      handleQuestUpdate({ message: message as ServerQuestUpdateMessage });
      break;
    case MessageType.SERVER_SKILL_XP_GAIN:
      handleSkillXpGain({ message: message as ServerSkillXpGainMessage });
      break;
    case MessageType.SERVER_WORLD_OBJECT_REMOVE:
      handleWorldObjectRemove({ message: message as ServerWorldObjectRemoveMessage });
      break;
    case MessageType.SERVER_WORLD_OBJECT_UPDATE:
      handleWorldObjectUpdate({ message: message as ServerWorldObjectUpdateMessage });
      break;
    case MessageType.SERVER_COLONY_LOST:
      handleColonyLost();
      break;
    case MessageType.SERVER_RESPAWN:
      handleRespawn({ message: message as ServerRespawnMessage });
      break;
  }
};

// Sync equipment state from entity snapshot
const syncEquipmentFromSnapshot = ({
  networkId,
  equippedWeapon,
  equippedPickaxe,
  equippedAxe,
}: {
  networkId: number;
  equippedWeapon: number | undefined;
  equippedPickaxe: number | undefined;
  equippedAxe: number | undefined;
}): void => {
  // Convert equippedWeapon ItemTypeValue to ItemData for the pawnGear system
  if (equippedWeapon !== undefined && equippedWeapon > 0) {
    const itemType = equippedWeapon as ItemTypeValue;
    const weaponItem: ItemData = {
      id: itemType,
      name: getItemName({ itemType }),
      icon: getItemIcon({ itemType }) ?? '',
      rarity: ItemRarity.COMMON,
      stackable: false,
      quantity: 1,
      slot: EquipmentSlot.PRIMARY,
      stats: { attack: 10 }, // TODO: Get actual stats from weapon definitions
    };
    equipItem({ pawnId: networkId, slot: EquipmentSlot.PRIMARY, item: weaponItem });
  } else {
    // No weapon equipped - clear the slot
    equipItem({ pawnId: networkId, slot: EquipmentSlot.PRIMARY, item: null });
  }

  const utilityItemType = equippedAxe && equippedAxe > 0 ? equippedAxe : equippedPickaxe;
  if (utilityItemType !== undefined && utilityItemType > 0) {
    const itemType = utilityItemType as ItemTypeValue;
    equipItem({
      pawnId: networkId,
      slot: EquipmentSlot.UTILITY,
      item: {
        id: itemType,
        name: getItemName({ itemType }),
        icon: getItemIcon({ itemType }) ?? '',
        rarity: ItemRarity.COMMON,
        stackable: false,
        quantity: 1,
        slot: EquipmentSlot.UTILITY,
      },
    });
  } else {
    equipItem({ pawnId: networkId, slot: EquipmentSlot.UTILITY, item: null });
  }
};

// Extract needs data from entity snapshot
const extractNeedsData = ({ entity }: { entity: EntitySnapshot }) => ({
  hunger: entity.hunger ?? 100,
  rest: entity.rest ?? 100,
  mood: entity.mood ?? 50,
});

// Extract status data from entity snapshot
const extractStatusData = ({ entity }: { entity: EntitySnapshot }) => ({
  isDowned: entity.isDowned ?? 0,
  isUnconscious: entity.isUnconscious ?? 0,
  carriedByNetworkId: entity.carriedByNetworkId ?? 0,
  carryingNetworkId: entity.carryingNetworkId ?? 0,
  restingInBedNetworkId: entity.restingInBedNetworkId ?? 0,
});

const syncTrackedSkillXp = ({
  pawnNetworkId,
  skills,
}: {
  pawnNetworkId: number;
  skills: ReadonlyArray<SkillSnapshot>;
}): void => {
  for (const skill of skills) {
    seedSkillXpFromLevel({
      pawnNetworkId,
      skillId: skill.id,
      level: skill.level,
    });
  }
};

const buildPawnUpdatesFromEntity = ({
  entity,
  skills,
  fallbackNeeds,
}: {
  entity: EntitySnapshot;
  skills: ReadonlyArray<SkillSnapshot>;
  fallbackNeeds?: {
    hunger: number;
    rest: number;
    mood: number;
  };
}): Partial<PawnData> => ({
  x: entity.x,
  y: entity.y,
  vx: entity.vx,
  vy: entity.vy,
  health: entity.health,
  maxHealth: entity.maxHealth,
  hunger: entity.hunger ?? fallbackNeeds?.hunger ?? 100,
  rest: entity.rest ?? fallbackNeeds?.rest ?? 100,
  mood: entity.mood ?? fallbackNeeds?.mood ?? 50,
  state: entity.state,
  path: entity.path ?? null,
  pathIndex: entity.pathIndex ?? 0,
  targetX: entity.targetX ?? null,
  targetY: entity.targetY ?? null,
  skills,
  task: entity.task ?? null,
  combatTarget: entity.combatTarget ?? 0,
  combatState: entity.combatState ?? 0,
  ...extractStatusData({ entity }),
});

// Convert EntitySnapshot to PawnData (adds client-only interpolation field)
const entitySnapshotToPawnData = ({ entity }: { entity: EntitySnapshot }): PawnData => ({
  networkId: entity.networkId,
  x: entity.x,
  y: entity.y,
  vx: entity.vx,
  vy: entity.vy,
  health: entity.health,
  maxHealth: entity.maxHealth,
  ...extractNeedsData({ entity }),
  state: entity.state,
  ownerId: entity.ownerId,
  name: entity.name ?? `Pawn ${entity.networkId}`,
  path: entity.path ?? null,
  pathIndex: entity.pathIndex ?? 0,
  targetX: entity.targetX ?? null,
  targetY: entity.targetY ?? null,
  interpolation: null,
  skills: entity.skills ?? [],
  task: entity.task ?? null,
  combatTarget: entity.combatTarget ?? 0,
  combatState: entity.combatState ?? 0,
  ...extractStatusData({ entity }),
});

const handleWelcome = ({ message }: { message: ServerWelcomeMessage }): void => {
  console.log('Received welcome from server:', message);

  // Reset reconnection attempts on successful connection (NOT connection status!)
  resetReconnectAttempts();
  updateServerTick({ tick: message.serverTick });
  updateServerTickRate({ tickRate: message.tickRate });

  setPlayerId({ id: message.playerId });

  // Add all initial entities
  for (const entity of message.initialEntities) {
    const pawnData = entitySnapshotToPawnData({ entity });
    syncTrackedSkillXp({ pawnNetworkId: pawnData.networkId, skills: pawnData.skills });
    addPawn({ pawn: pawnData });

    // Sync equipment state
    syncEquipmentFromSnapshot({
      networkId: entity.networkId,
      equippedWeapon: entity.equippedWeapon,
      equippedPickaxe: entity.equippedPickaxe,
      equippedAxe: entity.equippedAxe,
    });

    // Store entity state for delta compression
    setEntityState({ networkId: entity.networkId, entity });
  }

  // Check for colony-lost state on reconnect (no owned pawns)
  if (message.ownedPawnIds.length === 0) {
    setColonyLost();
  } else {
    clearColonyLost();
  }

  // Start ping interval
  startPingInterval();

  resources.callbacks.onWelcome?.({ message });
};

const handleSnapshot = ({ message }: { message: ServerSnapshotMessage }): void => {
  updateServerTick({ tick: message.tick });
  updateLastAcknowledgedInput({ sequence: message.lastProcessedInput });

  for (const entity of message.entities) {
    const existingPawn = getPawn({ networkId: entity.networkId });

    if (existingPawn) {
      const updatedSkills = entity.skills ?? existingPawn.skills;
      syncTrackedSkillXp({ pawnNetworkId: entity.networkId, skills: updatedSkills });
      updatePawn({
        networkId: entity.networkId,
        updates: buildPawnUpdatesFromEntity({
          entity,
          skills: updatedSkills,
          fallbackNeeds: {
            hunger: existingPawn.hunger,
            rest: existingPawn.rest,
            mood: existingPawn.mood,
          },
        }),
      });
    } else {
      const pawnData = entitySnapshotToPawnData({ entity });
      syncTrackedSkillXp({ pawnNetworkId: pawnData.networkId, skills: pawnData.skills });
      addPawn({ pawn: pawnData });
    }

    // Sync equipment state
    syncEquipmentFromSnapshot({
      networkId: entity.networkId,
      equippedWeapon: entity.equippedWeapon,
      equippedPickaxe: entity.equippedPickaxe,
      equippedAxe: entity.equippedAxe,
    });

    // Update stored entity state
    setEntityState({ networkId: entity.networkId, entity });
  }
};

// Update pawn from entity state
const updatePawnFromEntityState = ({
  networkId,
  newState,
}: {
  networkId: number;
  newState: EntitySnapshot;
}): void => {
  const existingPawn = getPawn({ networkId });
  const updatedSkills = newState.skills ?? existingPawn?.skills ?? [];
  syncTrackedSkillXp({ pawnNetworkId: networkId, skills: updatedSkills });
  updatePawn({
    networkId,
    updates: buildPawnUpdatesFromEntity({ entity: newState, skills: updatedSkills }),
  });
};

// Create entity snapshot from delta (for new entities)
const createEntityFromDelta = ({
  delta,
}: {
  delta: ServerDeltaSnapshotMessage['deltas'][number];
}): EntitySnapshot => ({
  networkId: delta.networkId,
  entityType: 1, // PAWN
  x: delta.x ?? 0,
  y: delta.y ?? 0,
  vx: delta.vx ?? 0,
  vy: delta.vy ?? 0,
  health: delta.health ?? 100,
  maxHealth: delta.maxHealth ?? 100,
  state: delta.state ?? 0,
  ownerId: 0,
  name: delta.name,
  hunger: delta.hunger,
  rest: delta.rest,
  mood: delta.mood,
  path: delta.path,
  pathIndex: delta.pathIndex,
  targetX: delta.targetX,
  targetY: delta.targetY,
  skills: delta.skills,
  task: delta.task,
  combatTarget: delta.combatTarget,
  combatState: delta.combatState,
  isDowned: delta.isDowned,
  isUnconscious: delta.isUnconscious,
  carriedByNetworkId: delta.carriedByNetworkId,
  carryingNetworkId: delta.carryingNetworkId,
  restingInBedNetworkId: delta.restingInBedNetworkId,
  equippedWeapon: delta.equippedWeapon,
  equippedPickaxe: delta.equippedPickaxe,
  equippedAxe: delta.equippedAxe,
});

// Process a single entity delta
const processEntityDelta = ({
  delta,
}: {
  delta: ServerDeltaSnapshotMessage['deltas'][number];
}): void => {
  const previousState = getEntityState({ networkId: delta.networkId });

  if (previousState) {
    const newState = applyEntityDelta({ entity: previousState, delta });
    setEntityState({ networkId: delta.networkId, entity: newState });
    updatePawnFromEntityState({ networkId: delta.networkId, newState });
    syncEquipmentFromSnapshot({
      networkId: delta.networkId,
      equippedWeapon: newState.equippedWeapon,
      equippedPickaxe: newState.equippedPickaxe,
      equippedAxe: newState.equippedAxe,
    });
  } else {
    const newEntity = createEntityFromDelta({ delta });
    setEntityState({ networkId: delta.networkId, entity: newEntity });
    const pawnData = entitySnapshotToPawnData({ entity: newEntity });
    syncTrackedSkillXp({ pawnNetworkId: pawnData.networkId, skills: pawnData.skills });
    addPawn({ pawn: pawnData });
    syncEquipmentFromSnapshot({
      networkId: delta.networkId,
      equippedWeapon: delta.equippedWeapon,
      equippedPickaxe: delta.equippedPickaxe,
      equippedAxe: delta.equippedAxe,
    });
  }
};

// Process NPC updates
const processNpcUpdates = ({ npcs }: { npcs: ServerDeltaSnapshotMessage['npcs'] }): void => {
  if (!npcs) {
    return;
  }

  for (const npc of npcs) {
    const existing = getNpc({ networkId: npc.networkId });
    if (existing) {
      updateNpc({
        networkId: npc.networkId,
        updates: {
          x: npc.x,
          y: npc.y,
          vx: npc.vx,
          vy: npc.vy,
          health: npc.health,
          maxHealth: npc.maxHealth,
          aiState: npc.aiState,
          hunger: npc.hunger,
          tiredness: npc.tiredness,
          fear: npc.fear,
          targetEntityId: npc.targetEntityId,
        },
      });
    } else {
      addNpc({ npc });
    }
  }
};

// Process removed NPCs
const processRemovedNpcs = ({
  removedNpcIds,
}: {
  removedNpcIds: ServerDeltaSnapshotMessage['removedNpcIds'];
}): void => {
  if (!removedNpcIds) {
    return;
  }

  for (const networkId of removedNpcIds) {
    removeNpc({ networkId });
    onNpcRemoved({ networkId });
  }
};

const handleDeltaSnapshot = ({ message }: { message: ServerDeltaSnapshotMessage }): void => {
  updateServerTick({ tick: message.tick });
  updateLastAcknowledgedInput({ sequence: message.lastProcessedInput });

  for (const delta of message.deltas) {
    processEntityDelta({ delta });
  }

  for (const networkId of message.removedIds) {
    deleteEntityState({ networkId });
    removePawn({ networkId });
    onPawnRemoved({ networkId });
  }

  processNpcUpdates({ npcs: message.npcs });
  processRemovedNpcs({ removedNpcIds: message.removedNpcIds });
};

const handlePong = ({ message }: { message: ServerPongMessage }): void => {
  const now = performance.now();
  const rtt = now - message.clientTime;

  // Add sample and update latency stats
  addRttSample({ rtt });

  // Update global latency signals for UI
  updateLatency({ rtt: rttMs.value, jitter: jitterMs.value });
};

const handleEntitySpawn = ({ message }: { message: ServerEntitySpawnMessage }): void => {
  // Create a minimal EntitySnapshot from spawn message
  const entity: EntitySnapshot = {
    networkId: message.networkId,
    entityType: message.entityType,
    x: message.x,
    y: message.y,
    vx: 0,
    vy: 0,
    health: 100,
    maxHealth: 100,
    state: 0,
    ownerId: message.ownerId ?? 0,
  };

  const pawnData = entitySnapshotToPawnData({ entity });
  syncTrackedSkillXp({ pawnNetworkId: pawnData.networkId, skills: pawnData.skills });
  addPawn({ pawn: pawnData });
};

const handleEntityDespawn = ({ networkId }: { networkId: number }): void => {
  deleteEntityState({ networkId });
  removePawn({ networkId });
  onPawnRemoved({ networkId });
};

const handleChunkData = ({ message }: { message: ServerChunkDataMessage }): void => {
  const chunk: ChunkData = {
    x: message.chunkX,
    y: message.chunkY,
    tiles: new Uint8Array(message.tiles),
  };

  setChunk({ chunk, objects: message.objects ?? [] });

  // Load ground items from chunk
  if (message.groundItems && message.groundItems.length > 0) {
    setGroundItems({ items: message.groundItems });
  }

  // Load construction sites from chunk
  if (message.constructionSites && message.constructionSites.length > 0) {
    setConstructionSites({ sites: message.constructionSites });
  }
};

const handleGroundItemSpawn = ({ message }: { message: ServerGroundItemSpawnMessage }): void => {
  setGroundItem({ item: message.item });
};

const handleGroundItemDespawn = ({
  message,
}: {
  message: ServerGroundItemDespawnMessage;
}): void => {
  removeGroundItem({ networkId: message.networkId });
};

const handleGroundItemUpdate = ({ message }: { message: ServerGroundItemUpdateMessage }): void => {
  updateGroundItemQuantity({
    networkId: message.networkId,
    quantity: message.quantity,
  });
};

const handleConstructionSiteSpawn = ({
  message,
}: {
  message: ServerConstructionSiteSpawnMessage;
}): void => {
  setConstructionSite({ site: message.site });
};

const handleConstructionSiteUpdate = ({
  message,
}: {
  message: ServerConstructionSiteUpdateMessage;
}): void => {
  updateConstructionSite({
    networkId: message.networkId,
    progress: message.progress,
    state: message.state,
    workersAssigned: message.workersAssigned,
  });
};

const handleConstructionSiteComplete = ({
  message,
}: {
  message: ServerConstructionSiteCompleteMessage;
}): void => {
  // Remove the construction site
  removeConstructionSite({ networkId: message.networkId });

  // Add the completed world object to the chunk
  addObjectToChunk({ object: message.worldObject });

  console.log(
    `Construction ${message.networkId} completed, object ${message.worldObject.networkId} created at (${message.worldObject.tileX}, ${message.worldObject.tileY})`
  );
};

const handleConstructionSiteCancel = ({
  message,
}: {
  message: ServerConstructionSiteCancelMessage;
}): void => {
  // Remove the construction site
  removeConstructionSite({ networkId: message.networkId });
  console.log(`Construction ${message.networkId} cancelled`);
};

const handleInventoryUpdate = ({ message }: { message: ServerInventoryUpdateMessage }): void => {
  // Convert server inventory format to client ItemData format
  const inventory: (ItemData | null)[] = [];

  for (const item of message.inventory) {
    const itemType = item.itemType as ItemTypeValue;
    const itemName = getItemName({ itemType });
    const iconFile = getItemIcon({ itemType });
    const iconPath = iconFile ? `/items/${iconFile}` : '';

    const itemData: ItemData = {
      id: item.itemType,
      name: itemName,
      icon: iconPath,
      rarity: ItemRarity.COMMON, // Default rarity for now
      stackable: item.quantity > 1,
      quantity: item.quantity,
    };
    inventory.push(itemData);
  }

  // Pad with nulls to fill remaining slots
  while (inventory.length < INVENTORY_SIZE) {
    inventory.push(null);
  }

  setInventory({ pawnId: message.pawnNetworkId, inventory });
  console.log(`Pawn ${message.pawnNetworkId} inventory updated: ${message.inventory.length} items`);
};

const handleTerritoryClaimed = ({ message }: { message: ServerTerritoryClaimedMessage }): void => {
  const territory = message.territory;
  setTerritory({
    territory: {
      networkId: territory.networkId,
      ownerId: territory.ownerId,
      territoryType: territory.territoryType as TerritoryTypeValue,
      centerX: territory.centerX,
      centerY: territory.centerY,
      radius: territory.radius,
      claimedAt: territory.claimedAt,
    },
  });
  console.log(
    `Territory ${territory.networkId} claimed by player ${territory.ownerId} at (${territory.centerX}, ${territory.centerY})`
  );
};

const handleTerritoryLost = ({ message }: { message: ServerTerritoryLostMessage }): void => {
  removeTerritory({ networkId: message.territoryNetworkId });
  console.log(`Territory ${message.territoryNetworkId} lost`);
};

const handleTerritoryUpdate = ({ message }: { message: ServerTerritoryUpdateMessage }): void => {
  const territories = message.territories.map((t) => ({
    networkId: t.networkId,
    ownerId: t.ownerId,
    territoryType: t.territoryType as TerritoryTypeValue,
    centerX: t.centerX,
    centerY: t.centerY,
    radius: t.radius,
    claimedAt: t.claimedAt,
  }));
  setTerritories({ items: territories });
  console.log(`Received ${territories.length} territory updates`);
};

const handleEntityDied = ({ message }: { message: ServerEntityDiedMessage }): void => {
  const { entityId: networkId, entityType, killedByEntityId } = message;

  if (entityType === EntityType.PAWN) {
    // Remove from client state
    deleteEntityState({ networkId });
    removePawn({ networkId });
    onPawnRemoved({ networkId });
    console.log(`Pawn ${networkId} died (killed by ${killedByEntityId ?? 'unknown'})`);
  } else if (entityType === EntityType.ANIMAL) {
    removeNpc({ networkId });
    onNpcRemoved({ networkId });
    console.log(`NPC ${networkId} died (killed by ${killedByEntityId ?? 'unknown'})`);
  }
};

const handleCorpseSpawn = ({ message }: { message: ServerCorpseSpawnMessage }): void => {
  addCorpse({
    snapshot: {
      networkId: message.networkId,
      x: message.x,
      y: message.y,
      originalEntityType: message.originalEntityType,
      originalSubType: message.originalSubType,
      originalOwnerId: message.originalOwnerId,
      name: message.name,
      killerName: message.killerName,
      causeOfDeath: message.causeOfDeath,
      deathTick: message.deathTick,
      rotProgress: message.rotProgress,
      rotStage: message.rotStage,
      meatYield: message.meatYield,
      leatherYield: message.leatherYield,
    },
  });
  console.log(
    `Corpse ${message.networkId} spawned at (${message.x}, ${message.y})${message.name ? ` - ${message.name}` : ''}`
  );
};

const handleCorpseUpdate = ({ message }: { message: ServerCorpseUpdateMessage }): void => {
  updateCorpse({
    networkId: message.networkId,
    updates: {
      rotProgress: message.rotProgress,
      rotStage: message.rotStage as RotStageValue,
      ...(message.meatYield !== undefined && { meatYield: message.meatYield }),
      ...(message.leatherYield !== undefined && { leatherYield: message.leatherYield }),
    },
  });
};

const handleCorpseDespawn = ({ message }: { message: ServerCorpseDespawnMessage }): void => {
  removeCorpse({ networkId: message.networkId });
  console.log(`Corpse ${message.networkId} despawned (reason: ${message.reason})`);
};

const handleServerWorkstationData = ({
  message,
}: {
  message: ServerWorkstationDataMessage;
}): void => {
  handleWorkstationData({
    workstationNetworkId: message.workstationNetworkId,
    workstationType: message.workstationType as WorkstationTypeValue,
    queue: message.queue,
    availableRecipeIds: message.availableRecipeIds,
    ...(message.fuel !== undefined ? { fuel: message.fuel } : {}),
  });
  console.log(
    `Received workstation data for ${message.workstationNetworkId}: ${message.queue.length} queued items`
  );
};

const handleServerCraftOrderQueued = ({
  message,
}: {
  message: ServerCraftOrderQueuedMessage;
}): void => {
  handleCraftOrderQueued({ job: message.job });
  console.log(`Craft order queued: ${message.job.recipeId} x${message.job.quantity}`);
};

const handleServerCraftOrderUpdate = ({
  message,
}: {
  message: ServerCraftOrderUpdateMessage;
}): void => {
  handleCraftOrderUpdate({
    workstationNetworkId: message.workstationNetworkId,
    orderId: message.orderId,
    progress: message.progress,
    quantityCompleted: message.quantityCompleted,
    ...(message.fuel !== undefined ? { fuel: message.fuel } : {}),
  });
};

const handleServerCraftOrderComplete = ({
  message,
}: {
  message: ServerCraftOrderCompleteMessage;
}): void => {
  handleCraftOrderComplete({
    workstationNetworkId: message.workstationNetworkId,
    orderId: message.orderId,
  });
  console.log(`Craft order ${message.orderId} complete`);
};

const handleServerCraftOrderCancelled = ({
  message,
}: {
  message: ServerCraftOrderCancelledMessage;
}): void => {
  handleCraftOrderCancelled({ orderId: message.orderId });
  console.log(`Craft order ${message.orderId} cancelled: ${message.reason}`);
};

// ============================================================================
// Stockpile Handlers
// ============================================================================

const handleStockpileFull = ({ message }: { message: ServerStockpileFullMessage }): void => {
  setStockpile({ items: message.items });
  console.log(`Received full stockpile with ${message.items.length} item types`);
};

const handleStockpileUpdate = ({ message }: { message: ServerStockpileUpdateMessage }): void => {
  updateStockpileItem({
    itemType: message.itemType as ItemTypeValue,
    quantity: message.newQuantity,
  });
  const sign = message.delta > 0 ? '+' : '';
  console.log(
    `Stockpile updated: item ${message.itemType} = ${message.newQuantity} (${sign}${message.delta})`
  );
};

// ============================================================================
// Projectile Handlers
// ============================================================================

const handleProjectileSpawn = ({ message }: { message: ServerProjectileSpawnMessage }): void => {
  addProjectile({ snapshot: message.projectile });
};

const handleProjectileDespawn = ({
  message,
}: {
  message: ServerProjectileDespawnMessage;
}): void => {
  removeProjectile({ networkId: message.projectileNetworkId });
};

const handleProjectileHit = ({ message }: { message: ServerProjectileHitMessage }): void => {
  // Remove the projectile
  removeProjectile({ networkId: message.projectileNetworkId });

  // TODO: Add hit effects (damage numbers, particles) in future
  console.log(
    `Projectile ${message.projectileNetworkId} hit at (${message.hitX.toFixed(1)}, ${message.hitY.toFixed(1)}) - ${message.damage} damage${message.wasCrit ? ' (CRIT)' : ''}`
  );
};

// ============================================================================
// Time Handlers
// ============================================================================

const handleTimeUpdate = ({ message }: { message: ServerTimeUpdateMessage }): void => {
  setGameTime({ hour: message.gameHour });
};

// ============================================================================
// Quest Message Handlers
// ============================================================================

const handleQuestsFull = ({ message }: { message: ServerQuestsFullMessage }): void => {
  setQuests({
    quests: message.quests.map((q) => ({
      type: q.type as import('@dreamgame/shared').QuestTypeValue,
      currentAmount: q.currentAmount,
      targetAmount: q.targetAmount,
      completed: q.completed,
    })),
  });
};

const handleQuestUpdate = ({ message }: { message: ServerQuestUpdateMessage }): void => {
  updateQuest({
    quest: {
      type: message.quest.type as import('@dreamgame/shared').QuestTypeValue,
      currentAmount: message.quest.currentAmount,
      targetAmount: message.quest.targetAmount,
      completed: message.quest.completed,
    },
  });
};

const handleSkillXpGain = ({ message }: { message: ServerSkillXpGainMessage }): void => {
  recordSkillXpGain({
    pawnNetworkId: message.pawnNetworkId,
    skillId: message.skillId,
    xpGained: message.xpGained,
    newTotalXp: message.newTotalXp,
  });

  const pawn = getPawn({ networkId: message.pawnNetworkId });
  if (pawn) {
    const updatedSkills = pawn.skills.map((skill) => {
      if (skill.id !== message.skillId) {
        return skill;
      }

      return {
        ...skill,
        level: message.newLevel,
      };
    });
    syncTrackedSkillXp({
      pawnNetworkId: message.pawnNetworkId,
      skills: updatedSkills,
    });
    upsertSkillTotalXp({
      pawnNetworkId: message.pawnNetworkId,
      skillId: message.skillId,
      totalXp: message.newTotalXp,
    });
    updatePawn({
      networkId: message.pawnNetworkId,
      updates: {
        skills: updatedSkills,
      },
    });
  }

  addXpDrop({
    pawnNetworkId: message.pawnNetworkId,
    skillId: message.skillId,
    xpGained: message.xpGained,
    leveledUp: message.leveledUp,
  });
};

// ============================================================================
// World Object Handlers
// ============================================================================

const handleWorldObjectRemove = ({
  message,
}: {
  message: ServerWorldObjectRemoveMessage;
}): void => {
  removeObjectFromChunk({
    networkId: message.networkId,
    tileX: message.tileX,
    tileY: message.tileY,
  });
};

const handleWorldObjectUpdate = ({
  message,
}: {
  message: ServerWorldObjectUpdateMessage;
}): void => {
  updateObjectInChunk({ object: message.worldObject });
  handleWorkstationWorldObjectUpdate({
    worldObject: message.worldObject,
    ...(message.fuel !== undefined ? { fuel: message.fuel } : {}),
  });
};

// ============================================================================
// Colony Lost / Respawn Handlers
// ============================================================================

const handleColonyLost = (): void => {
  setColonyLost();
  console.log('Colony lost - all pawns dead');
};

const handleRespawn = ({ message }: { message: ServerRespawnMessage }): void => {
  clearColonyLost();
  const pawnData = entitySnapshotToPawnData({ entity: message.pawn });
  addPawn({ pawn: pawnData });
  setEntityState({ networkId: message.pawn.networkId, entity: message.pawn });
  syncEquipmentFromSnapshot({
    networkId: message.pawn.networkId,
    equippedWeapon: message.pawn.equippedWeapon,
    equippedPickaxe: message.pawn.equippedPickaxe,
    equippedAxe: message.pawn.equippedAxe,
  });
  console.log(`Respawned with new pawn ${message.pawn.networkId}`);
};

// ============================================================================
// Message Sending
// ============================================================================

type ClientMessage =
  | ClientConnectMessage
  | ClientInputMessage
  | ClientPawnCommandMessage
  | ClientChunkRequestMessage
  | ClientPingMessage
  | ClientCameraUpdateMessage
  | ClientConstructionOrderMessage
  | ClientCancelConstructionMessage
  | ClientOpenWorkstationMessage
  | ClientCraftOrderMessage
  | ClientCancelCraftMessage
  | ClientPawnSelectionMessage
  | ClientRespawnMessage;

const send = (message: ClientMessage): void => {
  if (!resources.socket || resources.socket.readyState !== WebSocket.OPEN) {
    return;
  }

  const data = serializeMessage(message);
  resources.socket.send(data);
};

const sendConnect = ({ playerName }: { playerName: string }): void => {
  const message: ClientConnectMessage = {
    type: MessageType.CLIENT_CONNECT,
    tick: serverTick.value,
    playerName,
    protocolVersion: PROTOCOL_VERSION,
    sessionId: getSessionId(),
  };
  send(message);
};

const sendPing = (): void => {
  if (!isConnected()) {
    return;
  }

  const now = performance.now();
  updatePingTime({ time: now });

  const message: ClientPingMessage = {
    type: MessageType.CLIENT_PING,
    tick: serverTick.value,
    clientTime: now,
  };
  send(message);
};

const startPingInterval = (): void => {
  stopPingInterval();
  resources.pingIntervalId = setInterval(sendPing, PING_INTERVAL_MS);
};

const stopPingInterval = (): void => {
  if (resources.pingIntervalId) {
    clearInterval(resources.pingIntervalId);
    resources.pingIntervalId = null;
  }
};

// ============================================================================
// Reconnection
// ============================================================================

const calculateReconnectDelay = (): number => {
  // Exponential backoff with jitter
  const exponentialDelay = DEFAULT_RECONNECT_CONFIG.baseDelayMs * 2 ** reconnectAttempts.value;
  const cappedDelay = Math.min(exponentialDelay, DEFAULT_RECONNECT_CONFIG.maxDelayMs);
  // Add random jitter (0-25% of delay)
  const jitter = cappedDelay * Math.random() * 0.25;
  return cappedDelay + jitter;
};

const scheduleReconnect = (): void => {
  if (!reconnectEnabled.value) {
    return;
  }

  if (reconnectAttempts.value >= DEFAULT_RECONNECT_CONFIG.maxAttempts) {
    console.log('Max reconnection attempts reached');
    setFailed({ error: 'Connection failed after maximum retry attempts' });
    return;
  }

  const delay = calculateReconnectDelay();
  console.log(
    `Scheduling reconnection attempt ${reconnectAttempts.value + 1}/${DEFAULT_RECONNECT_CONFIG.maxAttempts} in ${Math.round(delay)}ms`
  );

  resources.reconnectTimeoutId = setTimeout(() => {
    incrementReconnectAttempts();
    connectInternal();
  }, delay);
};

const cancelReconnect = (): void => {
  if (resources.reconnectTimeoutId) {
    clearTimeout(resources.reconnectTimeoutId);
    resources.reconnectTimeoutId = null;
  }
};

const connectInternal = (): void => {
  if (resources.socket) {
    resources.socket.close();
  }

  setConnecting();

  resources.socket = new WebSocket(resources.url);
  resources.socket.binaryType = 'arraybuffer';

  resources.socket.onopen = () => {
    setConnected();
    sendConnect({ playerName: resources.playerName });
    resources.callbacks.onConnect?.();
  };

  resources.socket.onmessage = (event) => {
    if (event.data instanceof ArrayBuffer) {
      // Queue raw buffer for processing inside the game loop's rAF callback.
      // This prevents deserialization + state updates from running between
      // frames and delaying the next rAF.
      _messageQueue.push(event.data);
    }
  };

  resources.socket.onclose = () => {
    stopPingInterval();
    setDisconnected();
    resources.callbacks.onDisconnect?.();
    resources.socket = null;

    // Attempt reconnection
    scheduleReconnect();
  };

  resources.socket.onerror = (error) => {
    console.error('WebSocket error:', error);
    // onclose will be called after onerror, so reconnection is handled there
  };
};

// ============================================================================
// Public API
// ============================================================================

export const connect = ({
  url,
  playerName,
  onConnect,
  onDisconnect,
  onWelcome,
}: ConnectOptions): void => {
  // Cancel any pending reconnection
  cancelReconnect();

  // Reset state for new connection
  resources.url = url;
  resources.playerName = playerName ?? 'Player';
  resources.callbacks.onConnect = onConnect ?? null;
  resources.callbacks.onDisconnect = onDisconnect ?? null;
  resources.callbacks.onWelcome = onWelcome ?? null;

  // Reset all signal state
  resetConnectionState();
  setReconnectEnabled({ enabled: true });
  resetLatencyStats();
  clearEntityStates();
  clearXpDrops();
  clearSkillXpState();

  // Set server URL in global state for debug UI
  setServerUrl({ url });

  connectInternal();
};

export const disconnect = (): void => {
  // Disable reconnection when explicitly disconnecting
  setReconnectEnabled({ enabled: false });
  cancelReconnect();
  stopPingInterval();
  clearAllQueues();
  clearXpDrops();
  clearSkillXpState();

  if (resources.socket) {
    resources.socket.close();
    resources.socket = null;
  }
  setDisconnected();
};

export const isConnected = (): boolean => {
  return resources.socket?.readyState === WebSocket.OPEN;
};

export const getLatency = (): { rttMs: number; jitterMs: number } => {
  return {
    rttMs: rttMs.value,
    jitterMs: jitterMs.value,
  };
};

export const getLastAcknowledgedInput = (): number => {
  return lastPingTime.value;
};

export const sendInput = ({ input, tick }: { input: InputState; tick: number }): void => {
  if (!isConnected()) {
    return;
  }

  const sequence = incrementInputSequence();

  const message: ClientInputMessage = {
    type: MessageType.CLIENT_INPUT,
    tick,
    inputFlags: input.flags,
    mouseX: input.mouseWorldX,
    mouseY: input.mouseWorldY,
    sequence,
  };

  send(message);
};

export const sendCameraUpdate = ({
  cameraX,
  cameraY,
  viewWidth,
  viewHeight,
}: {
  cameraX: number;
  cameraY: number;
  viewWidth: number;
  viewHeight: number;
}): void => {
  if (!isConnected()) {
    return;
  }

  const message: ClientCameraUpdateMessage = {
    type: MessageType.CLIENT_CAMERA_UPDATE,
    tick: serverTick.value,
    cameraX,
    cameraY,
    viewWidth,
    viewHeight,
  };

  send(message);
};

// Optimistically clear combat state when sending non-combat commands
// Prevents stale combat indicators lingering until the next server snapshot
const optimisticClearCombat = ({
  pawnIds,
  commandType,
}: {
  pawnIds: number[];
  commandType: number;
}): void => {
  const isCombatCommand =
    commandType === PawnCommandType.ATTACK ||
    commandType === PawnCommandType.SET_COMBAT_MODE ||
    commandType === PawnCommandType.EQUIP_ITEM;

  if (isCombatCommand) {
    return;
  }

  for (const pawnId of pawnIds) {
    const pawn = getPawn({ networkId: pawnId });
    if (pawn && (pawn.combatTarget > 0 || pawn.combatState > 0)) {
      updatePawn({
        networkId: pawnId,
        updates: { combatTarget: 0, combatState: 0 },
      });
    }
  }
};

export const sendPawnCommand = ({
  pawnIds,
  commandType,
  targetX,
  targetY,
  targetEntity,
  queue,
}: {
  pawnIds: number[];
  commandType: number;
  targetX: number;
  targetY: number;
  targetEntity?: number;
  queue?: boolean;
}): void => {
  if (!isConnected()) {
    return;
  }

  const message: ClientPawnCommandMessage = {
    type: MessageType.CLIENT_PAWN_COMMAND,
    tick: serverTick.value,
    commandType,
    pawnIds,
    targetX,
    targetY,
    ...(targetEntity !== undefined && { targetEntity }),
    ...(queue === true && { queue: true }),
  };

  send(message);

  // Track command queue client-side for visualization
  const isNonQueueable =
    commandType === PawnCommandType.CANCEL ||
    commandType === PawnCommandType.SET_COMBAT_MODE ||
    commandType === PawnCommandType.EQUIP_ITEM;

  if (queue === true) {
    enqueueCommands({
      pawnIds,
      command: {
        commandType,
        targetX,
        targetY,
        targetEntity: targetEntity ?? 0,
      },
    });
  } else if (!isNonQueueable) {
    clearQueue({ pawnIds });
  }

  // Optimistically clear combat state when sending non-combat commands
  optimisticClearCombat({ pawnIds, commandType });
};

export const sendMoveCommand = ({
  pawnIds,
  targetX,
  targetY,
  queue,
}: {
  pawnIds: number[];
  targetX: number;
  targetY: number;
  queue?: boolean;
}): void => {
  sendPawnCommand({
    pawnIds,
    commandType: PawnCommandType.MOVE_TO,
    targetX,
    targetY,
    ...(queue === true && { queue: true }),
  });
};

export const requestChunk = ({ chunkX, chunkY }: { chunkX: number; chunkY: number }): void => {
  if (!isConnected()) {
    return;
  }

  markChunkPending({ x: chunkX, y: chunkY });

  const message: ClientChunkRequestMessage = {
    type: MessageType.CLIENT_CHUNK_REQUEST,
    tick: serverTick.value,
    chunkX,
    chunkY,
  };

  send(message);
};

export const sendConstructionOrder = ({
  objectType,
  tileX,
  tileY,
  pawnIds,
}: {
  objectType: ObjectTypeValue;
  tileX: number;
  tileY: number;
  pawnIds: number[];
}): void => {
  if (!isConnected()) {
    return;
  }

  const message: ClientConstructionOrderMessage = {
    type: MessageType.CLIENT_CONSTRUCTION_ORDER,
    tick: serverTick.value,
    objectType,
    tileX,
    tileY,
    pawnIds,
  };

  send(message);
};

export const sendCancelConstruction = ({ siteNetworkId }: { siteNetworkId: number }): void => {
  if (!isConnected()) {
    return;
  }

  const message: ClientCancelConstructionMessage = {
    type: MessageType.CLIENT_CANCEL_CONSTRUCTION,
    tick: serverTick.value,
    siteNetworkId,
  };

  send(message);
};

// ============================================================================
// Crafting Messages
// ============================================================================

export const sendOpenWorkstation = ({
  workstationNetworkId,
}: {
  workstationNetworkId: number;
}): void => {
  if (!isConnected()) {
    return;
  }

  const message: ClientOpenWorkstationMessage = {
    type: MessageType.CLIENT_OPEN_WORKSTATION,
    tick: serverTick.value,
    workstationNetworkId,
  };

  send(message);
};

export const sendCraftOrder = ({
  workstationNetworkId,
  recipeId,
  quantity,
  pawnIds,
}: {
  workstationNetworkId: number;
  recipeId: string;
  quantity: number;
  pawnIds: number[];
}): void => {
  if (!isConnected()) {
    return;
  }

  const message: ClientCraftOrderMessage = {
    type: MessageType.CLIENT_CRAFT_ORDER,
    tick: serverTick.value,
    workstationNetworkId,
    recipeId,
    quantity,
    pawnIds,
  };

  send(message);
};

export const sendCancelCraft = ({ orderId }: { orderId: number }): void => {
  if (!isConnected()) {
    return;
  }

  const message: ClientCancelCraftMessage = {
    type: MessageType.CLIENT_CANCEL_CRAFT,
    tick: serverTick.value,
    orderId,
  };

  send(message);
};

export const sendPawnSelection = ({
  selectedPawnIds,
}: {
  selectedPawnIds: ReadonlyArray<number>;
}): void => {
  if (!isConnected()) {
    return;
  }

  const message: ClientPawnSelectionMessage = {
    type: MessageType.CLIENT_PAWN_SELECTION,
    tick: serverTick.value,
    selectedPawnIds: [...selectedPawnIds],
  };

  send(message);
};

export const sendRespawn = (): void => {
  if (!isConnected()) {
    return;
  }

  const message: ClientRespawnMessage = {
    type: MessageType.CLIENT_RESPAWN,
    tick: serverTick.value,
  };

  send(message);
};
